import React from 'react'
import {
  UserInput,
  Application,
  StackFrame,
  getUserInputValue
} from '../../../attack-event/shared'
import { AttackEventCodeLocation } from '../../../attack-event/attack-event-details'
import { css } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../../../styled'
import { MustacheChapterView } from '../request/mustache-chapter-view'
import { Chapter } from '../drilldown-content-view'
import { FormattedMessage } from 'react-intl'
import { DownloadAttackValueLink } from '../../cell-content/download-attack-value-link'
import { logSuccess } from '../../../toaster'
import { ContrastFormattedMessage } from '../../../contrast-formatted-message'
import { Text } from '@contrast-security-inc/design-system-typography'
import { CopyButton } from '../../../../design-system'

export interface InputTracingAttackEventDetailsProps {
  organizationId: string
  eventUuid: string
  application: Application
  userInput?: UserInput
  file?: string
  method?: string
  line?: number
  stackFrames?: StackFrame[]
  lastCustomFrame?: number
  signature?: string
  vector?: string
}

const sectionHeaderCss = css`
  font-weight: ${deprecatedTheme.fontWeights.primary.bold};
  color: ${theme.color.neutral70};
`

const evidenceWellCss =
  'well evidence-well break-word ' +
  css`
    font-family: ${deprecatedTheme.fontFamilies.code};
    margin-bottom: 10px;
  `

const sectionHeaderContainerCss = css`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`

export function InputTracingAttackEventDetails(
  props: InputTracingAttackEventDetailsProps
) {
  const {
    organizationId,
    eventUuid,
    file,
    method,
    line,
    stackFrames,
    userInput,
    lastCustomFrame,
    signature,
    vector
  } = props

  return (
    <>
      {stackFrames && (
        <AttackEventCodeLocation
          file={file}
          method={method}
          line={line}
          stackFrames={stackFrames}
          lastCustomFrame={lastCustomFrame}
        />
      )}
      <InputTracingAttackValue
        organizationId={organizationId}
        eventUuid={eventUuid}
        userInput={userInput}
        signature={signature}
        vector={vector}
      />
    </>
  )
}

function InputTracingAttackValue({
  organizationId,
  eventUuid,
  userInput,
  signature,
  vector
}: {
  organizationId: string
  eventUuid: string
  userInput?: UserInput
  signature?: string
  vector?: string
}) {
  const attackValue = getUserInputValue(userInput)

  return (
    <>
      {attackValue && (
        <>
          <SectionWithHeading
            labelId="title_attackEvents_attackValue"
            content={() => (
              <Text font="mono">
                {userInput && userInput.truncatedValue
                  ? `${attackValue}...`
                  : attackValue}
              </Text>
            )}
            copyButton={() =>
              userInput &&
              (userInput.truncatedValue ? (
                <DownloadAttackValueLink
                  organizationID={organizationId}
                  eventUuid={eventUuid}
                  showTooltip={true}
                />
              ) : (
                <ContrastFormattedMessage
                  id="detail_successfullyCopiedAttackValue"
                  children={(successMesssage) => (
                    <CopyButton
                      stringToCopy={attackValue}
                      onClick={() => logSuccess(successMesssage)}
                    />
                  )}
                />
              ))
            }
          />
        </>
      )}
      {signature && vector && (
        <SectionWithHeading
          labelId="title_attackEventDetail_vectorAnalysis"
          chapter={{ type: '', body: vector }}
          containerClassName={css`
            margin-top: 20px;
          `}
        />
      )}
    </>
  )
}

function SectionWithHeading({
  labelId,
  content,
  copyButton,
  chapter,
  containerClassName
}: {
  labelId: string
  content?: () => React.ReactNode
  copyButton?: () => React.ReactNode
  chapter?: Chapter
  containerClassName?: string
}) {
  return (
    <FormattedMessage
      id={labelId}
      children={(label) => (
        <div className={containerClassName}>
          <div className={sectionHeaderContainerCss}>
            <span className={sectionHeaderCss}>{label}</span>
            {copyButton && copyButton()}
          </div>
          {content && <div className={evidenceWellCss}>{content()}</div>}
          {chapter && <MustacheChapterView chapter={chapter} />}
        </div>
      )}
    />
  )
}
